import { AuthenticationError } from '@multiplier/lib-shared-errors';
import { AUDIT_HEADERS } from '@multiplier/lib-shared-types';
import * as Sentry from '@sentry/react';
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { ulid } from 'ulid';
import { oktaAuth } from '@/lib/auth';
import { getAuthHeaders } from '@/lib/auth-utils';

type InternalAxiosRequestConfigWithRetry = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

const attachAuthHeader = (config: InternalAxiosRequestConfigWithRetry) => {
  try {
    Object.assign(config.headers, getAuthHeaders());
  } catch (_error) {
    Sentry.captureException(_error, { level: 'warning' });
  }

  return config;
};

const genericApiResponseErrorCallback = async (genericError: AxiosError) => {
  const response = genericError.response;

  if (response) {
    const originalRequest = genericError.config;

    switch (response.status) {
      case 401:
        return await handleUnauthenticatedError(originalRequest);
      default:
        return Promise.reject(genericError);
    }
  }

  return Promise.reject(genericError);
};

const handleUnauthenticatedError = async (originalRequest: InternalAxiosRequestConfigWithRetry | undefined) => {
  if (!originalRequest) {
    return Promise.reject(new AuthenticationError('Authentication expired. Please log in again.'));
  }

  // Already retried once — prevent infinite retry loops
  if (originalRequest._retry) {
    return Promise.reject(new AuthenticationError('Authentication expired. Please log in again.'));
  }

  originalRequest._retry = true;

  try {
    const authState = oktaAuth.authStateManager.getAuthState();

    if (authState?.isAuthenticated) {
      const renewedToken = await oktaAuth.tokenManager.renew('accessToken');

      if (renewedToken && 'accessToken' in renewedToken && originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${renewedToken.accessToken}`;
        return api(originalRequest);
      }

      return Promise.reject(new AuthenticationError('Authentication expired. Please log in again.'));
    }

    await oktaAuth.signInWithRedirect();
    return Promise.reject(
      new AuthenticationError('Authentication expired. No authentication Token found. Please log in again.')
    );
  } catch (tokenError) {
    Sentry.captureException(tokenError);
    await oktaAuth.signInWithRedirect();
    throw new AuthenticationError('Authentication expired. Please log in again.');
  }
};

const createApi = (baseURL: string) => {
  const _api = axios.create({
    baseURL,
    withCredentials: true,
    timeout: 30_000,
  });

  const _apiGet = <T>(path: string, config?: AxiosRequestConfig): Promise<T> => {
    return _api.get<T>(path, config).then((response) => response.data);
  };

  const _apiPost = <T>(path: string, data?: object, config?: AxiosRequestConfig): Promise<T> => {
    return _api.post<T>(path, data, config).then((response) => response.data);
  };

  const _apiPatch = <T>(path: string, data?: object, config?: AxiosRequestConfig): Promise<T> => {
    return _api.patch<T>(path, data, config).then((response) => response.data);
  };

  const _apiPut = <T>(path: string, data?: object, config?: AxiosRequestConfig): Promise<T> => {
    return _api.put<T>(path, data, config).then((response) => response.data);
  };

  const _apiDelete = <T>(path: string, config?: AxiosRequestConfig): Promise<T> => {
    return _api.delete<T>(path, config).then((response) => response.data);
  };

  return {
    api: _api,
    apiGet: _apiGet,
    apiPost: _apiPost,
    apiPatch: _apiPatch,
    apiPut: _apiPut,
    apiDelete: _apiDelete,
  };
};

export const { api, apiGet, apiPost, apiPatch, apiPut, apiDelete } = createApi('');

// Add request interceptor for audit context headers (correlation + request IDs)
const attachAuditHeaders = (config: InternalAxiosRequestConfig) => {
  config.headers[AUDIT_HEADERS.CORRELATION_ID] ??= `corr-${ulid()}`;
  config.headers[AUDIT_HEADERS.REQUEST_ID] = `req-${ulid()}`;
  return config;
};

api.interceptors.request.use(attachAuditHeaders);

// Add request interceptor for auth headers
api.interceptors.request.use(attachAuthHeader);

const apiResponseErrorCallback = async (error: AxiosError<{ message: string }>) => {
  return await genericApiResponseErrorCallback(error);
};

// Add response interceptor for error handling and token refresh
api.interceptors.response.use((response: AxiosResponse) => response, apiResponseErrorCallback);
